// -----------------------------------------------------------------------------
// engine/player.ts — the playback clock + preview renderer.
//
// This is the engine's "own clock" (CLAUDE.md §4 principle 5). Rendering is
// ON-DEMAND: the preview is re-composited whenever something changes (an edit, a
// seek, a load, or a freshly-decoded frame arriving), and a continuous
// requestAnimationFrame loop runs ONLY while playing. That keeps an idle editor
// from burning GPU every frame and means the preview is correct even in a
// backgrounded tab (where rAF is throttled). The UI calls play/pause/seek and
// requestRender(); it never draws frames itself.
// -----------------------------------------------------------------------------
import { projectDuration } from '../core/model';
import type { ProjectStore } from '../core/store';
import { Emitter } from '../shared/events';
import { clamp } from '../shared/time';
import { drawMediaLayer, drawTextLayer } from './compositor/layer';
import type { Compositor } from './compositor/gl';
import type { MediaManager } from './media/source';
import { compileRenderPlan } from './render-plan';

export interface PlayerEvents {
  time: number;
  playing: boolean;
}

export class Player {
  readonly events = new Emitter<PlayerEvents>();

  private rafId = 0;
  private playing = false;
  private currentTime = 0;
  private lastTs = 0;
  private mounted = false;

  constructor(
    private readonly store: ProjectStore,
    private readonly media: MediaManager,
    private readonly compositor: Compositor,
  ) {
    // A late-arriving decoded frame (async) should repaint the paused preview.
    this.media.onFrameReady = () => this.requestRender();
  }

  mount(): void {
    this.mounted = true;
    this.renderOnce(); // paint the initial frame
  }
  unmount(): void {
    this.mounted = false;
    this.stopLoop();
    this.playing = false;
  }

  /** Repaint now if we're paused; during playback the loop already repaints. */
  requestRender(): void {
    if (!this.playing) this.renderOnce();
  }

  private renderOnce(): void {
    if (!this.mounted) return;
    this.renderAt(this.currentTime);
  }

  private stopLoop(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  private loop = (ts: number): void => {
    if (!this.playing) {
      this.rafId = 0;
      return;
    }
    // Clamp dt so a stalled/throttled rAF gap can't make the playhead leap.
    const dt = this.lastTs ? Math.min((ts - this.lastTs) / 1000, 0.1) : 0;
    this.lastTs = ts;

    const duration = projectDuration(this.store.state.project);
    this.currentTime += dt;
    if (this.currentTime >= duration) {
      this.currentTime = duration;
      this.playing = false;
      this.events.emit('playing', false);
      this.events.emit('time', this.currentTime);
      this.renderOnce();
      this.rafId = 0;
      return;
    }
    this.events.emit('time', this.currentTime);
    this.renderAt(this.currentTime);
    this.rafId = requestAnimationFrame(this.loop);
  };

  // Composite a single frame at `time` into the preview canvas.
  private renderAt(time: number): void {
    const project = this.store.state.project;
    this.compositor.beginFrame(project.background);
    const textScale = this.compositor.height / project.height;
    const plan = compileRenderPlan(this.store.state, time);

    for (const layer of plan.layers) {
      if (layer.kind === 'media') {
        const source = this.media.getSource(layer.assetId);
        const bitmap = source?.peek(layer.sourceTimeSec);
        if (bitmap) drawMediaLayer(this.compositor, bitmap, layer.opacity);
      } else {
        drawTextLayer(this.compositor, layer, textScale);
      }
    }
  }

  // ---- transport -------------------------------------------------------------
  play(): void {
    if (this.playing) return;
    if (this.currentTime >= projectDuration(this.store.state.project)) this.currentTime = 0;
    this.playing = true;
    this.lastTs = 0;
    this.events.emit('playing', true);
    if (!this.rafId) this.rafId = requestAnimationFrame(this.loop);
  }
  pause(): void {
    if (!this.playing) return;
    this.playing = false;
    this.stopLoop();
    this.events.emit('playing', false);
    this.renderOnce();
  }
  toggle(): void {
    this.playing ? this.pause() : this.play();
  }
  seek(time: number): void {
    this.currentTime = clamp(time, 0, projectDuration(this.store.state.project));
    this.events.emit('time', this.currentTime);
    if (!this.playing) this.renderOnce();
  }
  getTime(): number {
    return this.currentTime;
  }
  isPlaying(): boolean {
    return this.playing;
  }
}
